/**
 * DemoRequestModal — "Book a 15-min demo" flow used by the landing
 * and pricing pages.
 *
 * Intentionally a native form (not a Calendly embed) so:
 *   • zero third-party dependencies to configure before launch
 *   • every submission lands in Mongo (`demo_requests`) and on the
 *     Super Admin queue — no scraping Calendly's dashboard
 *   • we can attribute it to PostHog + utm params for free
 *
 * Once real appointment scheduling matters, swap this for a Calendly
 * iframe; the /api/public/demo-request endpoint stays.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { analytics, EVENTS } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NURSERY_SIZES = [
  '1–20 children (childminder / micro)',
  '21–40 children',
  '41–80 children',
  '81–150 children',
  '150+ children (multi-site)',
];

const PREFERRED_WINDOWS = [
  'Mornings (9–12)',
  'Lunch break (12–14)',
  'Afternoons (14–17)',
  'Evenings (after 17)',
  'Any time — surprise me',
];

/**
 * Capture the current UTM parameters from the URL so we can attribute
 * this demo request back to the ad/campaign that drove it.
 */
function readUtmFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get('utm_source') || '',
      utm_medium: p.get('utm_medium') || '',
      utm_campaign: p.get('utm_campaign') || '',
      utm_content: p.get('utm_content') || '',
      utm_term: p.get('utm_term') || '',
    };
  } catch { return {}; }
}

export function DemoRequestModal({ open, onClose, source = 'unknown' }) {
  const [form, setForm] = useState({
    name: '', email: '', nursery: '', size: '', phone: '',
    preferred_time: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const utm = useMemo(readUtmFromUrl, [open]);

  // Reset local state whenever the modal is closed + reopened so a
  // returning visitor doesn't see the "thanks" state on their second
  // enquiry.
  useEffect(() => {
    if (open) {
      setDone(false);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const canSubmit = form.name.trim() && /@/.test(form.email);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/public/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ...utm, source }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong — please try again.');
      }
      analytics.track(EVENTS.DEMO_REQUESTED, {
        source,
        nursery_size: form.size || null,
        has_phone: !!form.phone,
        ...utm,
      });
      setDone(true);
    } catch (err) {
      toast.error('Could not submit request', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="demo-modal-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6, 43, 41, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        animation: 'kw-fade-in 180ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        style={{
          width: '100%', maxWidth: 520,
          background: 'white', borderRadius: 16,
          padding: '1.5rem',
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 30px 60px -20px rgba(6, 43, 41, 0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: 'hsl(180 40% 40%)', textTransform: 'uppercase' }}>
              Free · 15 minutes · no obligation
            </div>
            <h2 id="demo-modal-title" style={{ margin: '0.15rem 0 0', fontSize: '1.4rem', fontWeight: 700, color: 'hsl(180 40% 15%)' }}>
              {done ? 'Thanks — we\'ll be in touch' : 'Book a Kids WiseApp demo'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="demo-modal-close"
            style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'hsl(210 15% 40%)', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {done ? (
          <div data-testid="demo-modal-thanks" style={{ padding: '0.6rem 0 0.4rem' }}>
            <p style={{ margin: '0 0 1rem', color: 'hsl(210 15% 30%)', fontSize: '0.95rem', lineHeight: 1.55 }}>
              A member of our team will email you within one working day
              to book a 15-minute walkthrough. Look out for a note from
              <strong> hello@kidswiseapp.com</strong>.
            </p>
            <p style={{ margin: '0 0 1.25rem', color: 'hsl(210 15% 30%)', fontSize: '0.9rem' }}>
              In the meantime, you can also{' '}
              <a href="/pricing" style={{ color: 'hsl(180 65% 35%)', fontWeight: 600 }}>see pricing</a>
              {' '}or{' '}
              <a href="/signup" style={{ color: 'hsl(180 65% 35%)', fontWeight: 600 }}>start a free 14-day trial</a>
              .
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="demo-modal-thanks-close"
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} autoComplete="on">
            <p style={{ margin: '0.3rem 0 1rem', color: 'hsl(210 15% 40%)', fontSize: '0.88rem', lineHeight: 1.5 }}>
              Tell us a little about your setting and we'll book a 15-min
              walkthrough tailored to how you run bookings, invoicing and
              parent comms today.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Your name *</label>
                <input required autoFocus value={form.name} onChange={setField('name')} data-testid="demo-name" placeholder="Sarah Jones" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input required type="email" value={form.email} onChange={setField('email')} data-testid="demo-email" placeholder="sarah@bumblebee.co.uk" />
              </div>
              <div>
                <label className="form-label">Phone (optional)</label>
                <input type="tel" value={form.phone} onChange={setField('phone')} data-testid="demo-phone" placeholder="+44 7…" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Nursery name</label>
                <input value={form.nursery} onChange={setField('nursery')} data-testid="demo-nursery" placeholder="Bumblebee Day Nursery" />
              </div>
              <div>
                <label className="form-label">Size</label>
                <select value={form.size} onChange={setField('size')} data-testid="demo-size">
                  <option value="">Select…</option>
                  {NURSERY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Best time</label>
                <select value={form.preferred_time} onChange={setField('preferred_time')} data-testid="demo-time">
                  <option value="">Any time</option>
                  {PREFERRED_WINDOWS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Anything specific you'd like to see? (optional)</label>
                <textarea rows={3} value={form.message} onChange={setField('message')} data-testid="demo-message" placeholder="E.g. how invoicing + Mollie payments work end-to-end" />
              </div>
            </div>

            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
              <button
                type="submit"
                disabled={!canSubmit || busy}
                data-testid="demo-submit"
                className="btn btn-primary"
                style={{ padding: '0.75rem', fontSize: '0.95rem', fontWeight: 600 }}
              >
                {busy ? 'Sending…' : 'Request a demo →'}
              </button>
              <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'hsl(210 15% 45%)' }}>
                We reply within one working day. No spam, no dark patterns.
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default DemoRequestModal;
